import React, { useEffect, useRef, useState } from 'react'
import { FaCamera, FaCheckCircle, FaComments, FaDirections, FaMapMarkerAlt, FaPhoneAlt, FaUndoAlt } from 'react-icons/fa'
import { Banner, ChipSelect, Field, FoBtn, KV, Pill, QtyStepper, ScreenShell, Section, Sheet, TextArea } from './fieldUi'
import { FarForm, LinesTable, PhotoStrip, ReturnsMini, minutesLabel, useNow } from './shared'
import Signature from './Signature'
import { positionAtCheckIn, useField } from './fieldContext'
import { proximity } from '../store'
import { clockOfStamp, fmtMiles, fmtMinutes, driveMinutes, mapsNavigateUrl, roadMiles, telUrl } from '../geo'
import { FAIL_REASONS } from '../demoData'
import { addDays, fmtDate, lb, money2, weekday } from '../FieldOpsKit'

const FAR_REASONS = ['Parking is further away', 'Customer asked me to unload elsewhere', 'GPS is inaccurate here', 'Other']
const MANUAL_REASONS = ['Location is off on my phone', 'No GPS signal here', 'Other']

/**
 * One delivery stop for the driver: arrive (check-in), deliver line by line, take back returns, get the signature and
 * the payment (COD), or register a failed attempt. `readOnly` = from History.
 */
export default function DeliveryScreen({ id, readOnly, autoCheckin }) {
    const f = useField()
    const { state, nav, actor, gps, date } = f
    const field = state.field
    const d = state.drivers.deliveries.filter((x) => x.id === id)[0]
    const now = useNow(30000)
    const [short, setShort] = useState({})       // sku -> units NOT delivered
    const [receivedBy, setReceivedBy] = useState('')
    const [signed, setSigned] = useState(false)
    const [noSign, setNoSign] = useState('')
    const [method, setMethod] = useState('')
    const [paid, setPaid] = useState('')
    const [notes, setNotes] = useState('')
    const [photos, setPhotos] = useState([])
    const [tried, setTried] = useState(false)
    const [far, setFar] = useState(null)
    const [manual, setManual] = useState(false)
    const [failing, setFailing] = useState(false)
    const [saved, setSaved] = useState(false)
    const fileRef = useRef(null)
    const autoRan = useRef(false)

    const status = d ? (d.status === 'delivered' ? 'done' : d.status === 'out' && d.arrivedAt ? 'arrived' : 'todo') : 'none'
    const otherCurrent = f.stops.filter((s) => s.state === 'current' && (!d || s.id !== d.id))[0] || null
    const isToday = d ? d.date === date : false
    const prox = d && gps.pos ? proximity(gps.pos, d) : null
    const nextAfter = f.stops.filter((s) => s.state === 'pending' && (!d || s.id !== d.id))[0] || null

    useEffect(() => {
        if (autoRan.current || !autoCheckin || !d || status !== 'todo' || readOnly) return
        autoRan.current = true
        startArrive()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    if (!d) return <ScreenShell title="Delivery not found" onBack={nav.pop}><Banner tone="warn">This delivery is not available.</Banner></ScreenShell>

    const commitArrive = (pos, extra) => {
        f.send(Object.assign({ type: 'DELIVERY_ARRIVE', id: d.id, actorKey: actor.key, date: d.date, by: actor.name, lat: pos ? pos.lat : d.lat, lng: pos ? pos.lng : d.lng, distMi: pos ? proximity(pos, d).distMi : null, far: false }, extra || {}))
        f.toast('Arrived at ' + d.customer + ' · ' + clockOfStamp(Date.now()))
        setFar(null); setManual(false)
    }
    function startArrive() {
        if (otherCurrent) { f.toast('Finish the delivery at ' + otherCurrent.name + ' first.', 'warn'); return }
        if (!isToday) { f.toast('You can only start a delivery on its day.', 'warn'); return }
        const pos = positionAtCheckIn(field, d, gps.pos)
        if (!pos) { setManual(true); return }
        const p = proximity(pos, d)
        if (p.far) { setFar({ pos: pos, prox: p }); return }
        commitArrive(pos)
    }

    const shortLines = d.lines.filter((l) => (short[l.sku] || 0) > 0).map((l) => ({ sku: l.sku, qty: short[l.sku] }))
    const delivered = d.lines.reduce((a, l) => a + (l.qty - (short[l.sku] || 0)) * l.price, 0)
    const cod = d.terms === 'COD'
    const paidNum = paid === '' ? (cod ? delivered : 0) : Number(paid)
    const needSig = !signed && !noSign
    const errs = []
    if (!receivedBy.trim()) errs.push('Who received it?')
    if (needSig) errs.push('Signature (or why there is none)')
    if (cod && !method && paidNum > 0) errs.push('Payment method')

    const complete = () => {
        setTried(true)
        if (errs.length) { f.toast('Missing: ' + errs[0], 'warn'); return }
        f.send({ type: 'DELIVERY_COMPLETE', id: d.id, by: actor.name, receivedBy: receivedBy, signed: signed, shortLines: shortLines, payment: cod && paidNum > 0 ? { amount: paidNum, method: method } : null, photos: photos.length, note: [noSign ? 'No signature: ' + noSign : '', notes].filter(Boolean).join(' · ') })
        setSaved(true)
    }
    const onPhoto = (e) => { const file = e.target.files && e.target.files[0]; if (!file) return; setPhotos(photos.concat([URL.createObjectURL(file)])); e.target.value = '' }

    const retryOptions = []
    let dd = d.date
    for (let i = 0; i < 4; i++) { dd = addDays(dd, 1); if (weekday(dd) !== 0 && weekday(dd) !== 6) retryOptions.push(dd) }

    const header = (
        <div className="fo-storehead">
            <div><strong>{d.customer}</strong><span>{d.address}, {d.city}</span></div>
            <div className="fo-storehead__chips"><Pill tone="blue">{d.invoiceNo}</Pill><Pill tone="blue">{d.window}</Pill><Pill tone="gray">{lb(d.weightLb)}</Pill><Pill tone={cod ? 'amber' : 'gray'}>{cod ? 'COD ' + money2(d.amount) : d.terms}</Pill></div>
            <div className="fo-storehead__act">
                <FoBtn kind="ghost" small icon={<FaDirections />} href={mapsNavigateUrl(d.lat, d.lng)} newTab>Navigate</FoBtn>
                <FoBtn kind="ghost" small icon={<FaPhoneAlt />} href={telUrl(d.phone)}>{d.phone}</FoBtn>
            </div>
            {d.notes ? <div className="fo-note">{d.notes}</div> : null}
        </div>
    )

    let body = null
    let footer = null
    if (status === 'todo' && !readOnly) {
        body = (
            <React.Fragment>
                {header}
                {otherCurrent ? <Banner tone="warn" title="You are still at another stop.">Finish {otherCurrent.name} first.</Banner> : null}
                {!isToday ? <Banner tone="info">This delivery is planned for {fmtDate(d.date)}.</Banner> : null}
                {gps.source === 'denied' ? <Banner tone="warn">Location is off. You can still mark your arrival manually.</Banner> : prox ? <div className="fo-prox">{prox.far ? <FaMapMarkerAlt style={{ color: '#D97706' }} /> : <FaCheckCircle style={{ color: '#059669' }} />} <span>You are about <strong>{fmtMiles(roadMiles(gps.pos, d))}</strong> away (~{fmtMinutes(driveMinutes(roadMiles(gps.pos, d)))} by car).</span></div> : null}
                <Section title="Invoice"><LinesTable lines={d.lines} /></Section>
                <Section title="Contact"><KV label="Customer">{d.contact}</KV><KV label="Phone">{d.phone}</KV></Section>
            </React.Fragment>
        )
        footer = <React.Fragment><FoBtn kind="ghost" onClick={() => setFailing(true)}>Can’t deliver</FoBtn><FoBtn kind="primary" big onClick={startArrive} disabled={!!otherCurrent || !isToday}>I arrived</FoBtn></React.Fragment>
    } else if (status === 'arrived' && !readOnly) {
        const mins = Math.max(0, Math.round((now - d.arrivedAt) / 60000))
        body = (
            <React.Fragment>
                <div className="fo-timer"><div style={{ flex: 1 }}><strong>{minutesLabel(mins)}</strong><span>at the stop since {clockOfStamp(d.arrivedAt)}</span></div>{d.pending ? <Pill tone="amber">To send</Pill> : <Pill tone="cyan">Arrived</Pill>}</div>
                {mins >= 90 ? <Banner tone="warn" title="Long stop.">You have been at this stop for {minutesLabel(mins)}. Complete it or register a failed attempt.</Banner> : null}
                {d.far ? <Banner tone="warn" title="Far from the address.">Arrived {d.distMi ? fmtMiles(d.distMi) : ''} away. Reason: {d.farReason || '—'}</Banner> : null}
                {header}
                {cod ? <Banner tone="warn" title={'Collect ' + money2(paidNum || delivered) + ' on delivery.'}>This customer pays cash on delivery.</Banner> : null}
                <Section title="Check the products" right={<Pill tone={shortLines.length ? 'amber' : 'green'}>{shortLines.length ? shortLines.length + ' short' : 'All complete'}</Pill>}>
                    {d.lines.map((l) => {
                        const s = short[l.sku] || 0
                        return (
                            <div key={l.sku} className={'fo-chk' + (s ? ' is-short' : '')}>
                                <div><strong>{l.name}</strong><span>{l.qty} on the invoice · {money2(l.price)}</span></div>
                                <div className="fo-chk__q"><span>Delivered</span><QtyStepper value={l.qty - s} min={0} max={l.qty} onChange={(v) => setShort({ ...short, [l.sku]: l.qty - v })} label={l.name} /></div>
                            </div>
                        )
                    })}
                    <KV label="Value delivered" strong>{money2(delivered)}</KV>
                    {shortLines.length ? <Banner tone="warn">Short lines are reported to the office as a partial delivery.</Banner> : null}
                </Section>
                <Section title="Returns">
                    <div className="fo-actions-grid"><button type="button" className="fo-tile" onClick={() => nav.push({ type: 'return', source: 'delivery', refId: d.id })}><FaUndoAlt /><strong>Customer returns something</strong><span>Damaged, wrong item, expired…</span></button>
                        <button type="button" className="fo-tile" onClick={() => { f.send({ type: 'CHAT_SEND', actorKey: actor.key, from: 'worker', by: actor.name, text: 'Question about ' + d.invoiceNo + ' (' + d.customer + '): ', topic: 'general', ref: { type: 'delivery', id: d.id, label: d.invoiceNo } }); f.toast('Message sent to the office.') }}><FaComments /><strong>Message the office</strong><span>Price, complaint, change…</span></button></div>
                    {(d.retIds || []).length ? <div style={{ marginTop: 10 }}><ReturnsMini ids={d.retIds} state={state} /></div> : null}
                </Section>
                <Section title="Proof of delivery">
                    <Field label="Received by" error={tried && !receivedBy.trim() ? 'Write who received it' : ''}>
                        <input className="fo-input" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} placeholder="Full name" />
                        <div style={{ marginTop: 8 }}><ChipSelect options={[d.contact]} value={receivedBy} onChange={(v) => setReceivedBy(v || '')} /></div>
                    </Field>
                    <Field label="Signature" error={tried && needSig ? 'Get the signature, or say why there is none' : ''}>
                        <Signature onChange={setSigned} />
                        {!signed ? <div style={{ marginTop: 8 }}><ChipSelect options={['Customer refused to sign', 'Nobody to sign (left at the door)']} value={noSign} onChange={setNoSign} /></div> : null}
                    </Field>
                    <Field label="Photos">
                        <FoBtn kind="soft" icon={<FaCamera />} onClick={() => fileRef.current && fileRef.current.click()}>Take a photo of the delivery</FoBtn>
                        <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onPhoto} />
                        {photos.length ? <div className="fo-photos">{photos.map((t, i) => <img key={i} src={t} alt={'Delivery photo ' + (i + 1)} className="fo-photo fo-photo--img" />)}</div> : null}
                    </Field>
                </Section>
                {cod ? (
                    <Section title="Payment on delivery">
                        <Field label="Amount collected"><input className="fo-input" type="number" inputMode="decimal" value={paid === '' ? delivered.toFixed(2) : paid} onChange={(e) => setPaid(e.target.value)} /></Field>
                        <Field label="Method" error={tried && !method && paidNum > 0 ? 'Pick how they paid' : ''}><ChipSelect options={['Cash', 'Check', 'Card']} value={method} onChange={setMethod} /></Field>
                        {paidNum < delivered - 0.01 ? <Banner tone="warn">Less than the delivered value. The difference is reported to the office.</Banner> : null}
                    </Section>
                ) : null}
                <Section title="Notes"><TextArea value={notes} onChange={setNotes} rows={2} placeholder="Optional" /></Section>
            </React.Fragment>
        )
        footer = <React.Fragment><FoBtn kind="ghost" onClick={() => setFailing(true)}>Failed attempt</FoBtn><FoBtn kind="success" big onClick={complete}>Complete delivery</FoBtn></React.Fragment>
    } else {
        body = (
            <React.Fragment>
                {header}
                <Section title="Summary" right={<Pill tone={d.status === 'delivered' ? (d.shortLines && d.shortLines.length ? 'amber' : 'green') : 'gray'}>{d.status === 'delivered' ? (d.shortLines && d.shortLines.length ? 'Partial delivery' : 'Delivered') : d.status}</Pill>}>
                    <KV label="Date">{fmtDate(d.deliveredOn || d.date)}</KV>
                    {d.arrivedAt ? <KV label="Arrived">{clockOfStamp(d.arrivedAt)}</KV> : null}
                    {d.doneAt ? <KV label="Finished">{clockOfStamp(d.doneAt)}</KV> : null}
                    {d.arrivedAt && d.doneAt ? <KV label="Time at the stop">{minutesLabel(Math.max(1, Math.round((d.doneAt - d.arrivedAt) / 60000)))}</KV> : null}
                    {d.receivedBy ? <KV label="Received by">{d.receivedBy}{d.signed ? ' · signed ✓' : ''}</KV> : null}
                    {d.payment ? <KV label="Collected">{money2(d.payment.amount)} · {d.payment.method}</KV> : null}
                    {d.distMi !== null && d.distMi !== undefined ? <KV label="Distance at arrival">{d.far ? <Pill tone="amber">{fmtMiles(d.distMi)} away</Pill> : 'At the address'}</KV> : null}
                    <PhotoStrip count={d.photos} />
                </Section>
                <Section title="Invoice"><LinesTable lines={d.lines} shortLines={d.shortLines} /></Section>
                {(d.retIds || []).length ? <Section title="Returns"><ReturnsMini ids={d.retIds} state={state} /></Section> : null}
                <Section title="History">{d.events.slice().reverse().map((e, i) => <KV key={i} label={clockOfStamp(e.ts)}>{e.type}{e.note ? ' · ' + e.note : ''}</KV>)}</Section>
            </React.Fragment>
        )
        footer = <FoBtn kind="ghost" onClick={nav.pop}>Back</FoBtn>
    }

    return (
        <ScreenShell title={d.customer} subtitle={d.invoiceNo + ' · ' + (status === 'arrived' ? 'At the stop' : status === 'done' ? 'Delivered' : 'Delivery')} onBack={nav.pop} footer={saved ? null : footer}>
            {body}
            <Sheet show={!!far} onClose={() => setFar(null)} title="You are far from the address" subtitle={far ? 'About ' + fmtMiles(far.prox.distMi) + ' away' : ''}>
                <FarForm title="Mark arrival anyway" reasons={FAR_REASONS} onCancel={() => setFar(null)} onConfirm={(r) => commitArrive(far.pos, { far: true, distMi: far.prox.distMi, farReason: r })} />
            </Sheet>
            <Sheet show={manual} onClose={() => setManual(false)} title="Arrive without location" subtitle="The office will see it as a manual arrival">
                <FarForm title="Mark arrival" reasons={MANUAL_REASONS} onCancel={() => setManual(false)} onConfirm={(r) => commitArrive(null, { manual: true, farReason: r })} />
            </Sheet>
            {failing ? <FailSheet delivery={d} options={retryOptions} onClose={() => setFailing(false)} onDone={() => { setFailing(false); nav.pop() }} /> : null}
            <Sheet show={saved} onClose={() => { setSaved(false); nav.pop() }} title="Delivered ✓" subtitle={d.customer + (shortLines.length ? ' · partial' : '')}
                footer={<React.Fragment><FoBtn kind="ghost" onClick={() => { setSaved(false); nav.pop() }}>Back to my route</FoBtn>{nextAfter ? <FoBtn kind="primary" icon={<FaDirections />} href={mapsNavigateUrl(nextAfter.lat, nextAfter.lng)} newTab>Navigate to next</FoBtn> : null}</React.Fragment>}>
                {nextAfter ? <Banner tone="info" title={'Next: #' + nextAfter.num + ' ' + nextAfter.name + '.'}>{fmtMiles(roadMiles(d, nextAfter))} · {fmtMinutes(driveMinutes(roadMiles(d, nextAfter)))} from here.</Banner> : <Banner tone="ok" title="That was your last delivery.">Close the day from the Route tab (and hand over cash and returns).</Banner>}
            </Sheet>
        </ScreenShell>
    )
}

const FailSheet = ({ delivery, options, onClose, onDone }) => {
    const f = useField()
    const [reason, setReason] = useState('')
    const [date, setDate] = useState(options[0])
    const [note, setNote] = useState('')
    const [tried, setTried] = useState(false)
    const save = () => {
        setTried(true)
        if (!reason) return
        f.dispatch({ type: 'DELIVERY_STATUS', id: delivery.id, status: 'failed', reason: reason, note: note, newDate: date, by: f.actor.name })
        f.send({ type: 'CHAT_SEND', actorKey: f.actor.key, from: 'worker', by: f.actor.name, text: 'Could not deliver ' + delivery.invoiceNo + ' (' + delivery.customer + '): ' + reason + '. Retry ' + fmtDate(date) + '.' + (note ? ' ' + note : ''), topic: 'incident', ref: { type: 'delivery', id: delivery.id, label: delivery.invoiceNo } })
        f.toast('Failed attempt registered. Retry ' + fmtDate(date) + '.', 'warn')
        onDone()
    }
    return (
        <Sheet show title="Delivery not completed" subtitle={delivery.customer + ' · goes back to planned on the date you pick'} onClose={onClose}
            footer={<React.Fragment><FoBtn kind="ghost" onClick={onClose}>Cancel</FoBtn><FoBtn kind="danger" onClick={save}>Register failed attempt</FoBtn></React.Fragment>}>
            <Field label="Reason" error={tried && !reason ? 'Pick a reason' : ''}><ChipSelect options={FAIL_REASONS} value={reason} onChange={setReason} /></Field>
            <Field label="Try again on"><div className="fo-chips">{options.map((o) => <button key={o} type="button" className={'fo-chip' + (date === o ? ' is-on' : '')} onClick={() => setDate(o)}>{fmtDate(o)}</button>)}</div></Field>
            <Field label="Note (optional)"><TextArea value={note} onChange={setNote} rows={2} placeholder="e.g. Store manager asked to come back after 3 PM" /></Field>
        </Sheet>
    )
}
